/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState } from 'react';

import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';

import {
  Row, Col, Label, Alert, UncontrolledPopover, PopoverBody,
} from 'reactstrap';

import toastr from 'toastr';
import 'toastr/build/toastr.min.css';

import {
  Formik, Field, Form, ErrorMessage,
} from 'formik';
import * as Yup from 'yup';

import { post } from '../../helpers/api_helper';
import SqlEditor from './SqlEditor';

function SQLForm(props) {
  const {
    entity,
    t,
    onSuccess,
  } = props;

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const variableExample = `-- @start_date:date = 'yesterday'
-- @limit:number = 100
WHERE created_at >= {{start_date}}
LIMIT {{limit}}`;
  const tagVariableExample = `-- @tags_any:tags_any:listing_tags(listing_id=l.id,tag_id=tag_id) = Washer Parts
-- @tags_all:tags_all:item_tags(item_id=i.id,tag_id=tag_id) = Clearance, Refurbished`;
  const variableShortcuts = [
    { key: 'date', label: t('Date'), template: "-- @new_date:date = 'today'" },
    { key: 'datetime-local', label: t('DateTime'), template: "-- @new_datetime:datetime-local = 'today'" },
    { key: 'number', label: t('Number'), template: '-- @new_number:number = 100' },
    { key: 'text', label: t('Text'), template: "-- @new_text:text = 'value'" },
    {
      key: 'tags_any_item',
      label: t('Tags Any Item'),
      template: '-- @new_tags_any:tags_any:item_tags(item_id=i.id,tag_id=tag_id) = Washing Machine Parts',
    },
    {
      key: 'tags_any_listing',
      label: t('Tags Any Listing'),
      template: '-- @new_tags_any:tags_any:listing_tags(listing_id=l.id,tag_id=tag_id) = Washing Machine Parts',
    },
    {
      key: 'tags_all_item',
      label: t('Tags All Item'),
      template: '-- @new_tags_all:tags_all:item_tags(item_id=i.id,tag_id=tag_id) = Washing Machine Parts',
    },
    {
      key: 'tags_all_listing',
      label: t('Tags All Listing'),
      template: '-- @new_tags_all:tags_all:listing_tags(listing_id=l.id,tag_id=tag_id) = Washing Machine Parts',
    },
  ];

  toastr.options = {
    positionClass: 'toast-top-right',
    newestOnTop: true,
  };

  const save = async (values) => {
    try {
      setLoading(true);

      let result = null;
      if (entity.id !== undefined) {
        result = await post(`/api/sql/save/${entity.id}`, values);
      } else {
        result = await post('/api/sql/create', values);
      }

      setErrorMessage(null);
      toastr.success(t(`SQL query ${values.name} saved`), t('Success'));
      onSuccess(result);
    } catch (error) {
      setErrorMessage(`${error.message}: ${error.response?.data || ''}`);
    } finally {
      setLoading(false);
    }
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(t('Name is required')),
    description: Yup.string().nullable(),
    query: Yup.string().required(t('SQL query is required')),
  });

  const insertVariableTemplate = (currentQuery, template, setFieldValue) => {
    const trimmedQuery = currentQuery || '';
    const nextQuery = trimmedQuery.trim() === ''
      ? `${template}\n\n`
      : `${template}\n${trimmedQuery}`;

    setFieldValue('query', nextQuery);
  };

  return (
    <>
      <style>
        {`
          .sql-query-help-popover .popover-body pre,
          .sql-query-help-popover .popover-body code {
            color: #212529;
          }

          body[data-layout-mode="dark"] .sql-query-help-popover .popover-content,
          body[data-layout-mode="dark"] .sql-query-help-popover .popover-body {
            background: linear-gradient(180deg, rgba(51, 52, 46, 0.98) 0%, rgba(43, 44, 39, 0.98) 100%);
            color: #f3efe4;
          }

          body[data-layout-mode="dark"] .sql-query-help-popover .popover-header {
            background: rgba(51, 52, 46, 0.98);
            border-bottom-color: rgba(83, 84, 77, 0.85);
            color: #f3efe4;
          }

          body[data-layout-mode="dark"] .sql-query-help-popover[x-placement^="right"] .arrow::after,
          body[data-layout-mode="dark"] .sql-query-help-popover[data-popper-placement^="right"] .arrow::after {
            border-right-color: rgba(51, 52, 46, 0.98);
          }

          body[data-layout-mode="dark"] .sql-query-help-popover[x-placement^="right"] .arrow::before,
          body[data-layout-mode="dark"] .sql-query-help-popover[data-popper-placement^="right"] .arrow::before {
            border-right-color: rgba(83, 84, 77, 0.85);
          }

          body[data-layout-mode="dark"] .sql-query-help-popover .popover-body pre,
          body[data-layout-mode="dark"] .sql-query-help-popover .popover-body code {
            background: #252621;
            color: #f4f1e6;
          }
        `}
      </style>
      {errorMessage && (
        <Alert color="danger" className="mt-3" isOpen>
          {errorMessage}
        </Alert>
      )}
      <Formik
        enableReinitialize
        initialValues={entity}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          save(values);
        }}
      >
        {({
          touched, errors, values, setFieldValue,
        }) => (
          <Form className="mt-3" id="SQLForm" autoComplete="off">
            <Row className="mt-3 mb-3">
              <Label for="name" className="col-sm-2 col-form-label">
                {t('Name')}
              </Label>
              <Col sm={10}>
                <Field
                  type="text"
                  className={`form-control ${(errors.name && touched.name ? 'is-invalid' : '')}`}
                  id="name"
                  name="name"
                />
                <ErrorMessage name="name" component="div" className="text-danger" />
              </Col>
            </Row>

            <Row className="mt-3 mb-3">
              <Label for="description" className="col-sm-2 col-form-label">
                {t('Description')}
              </Label>
              <Col sm={10}>
                <Field
                  as="textarea"
                  rows="3"
                  className={`form-control ${(errors.description && touched.description ? 'is-invalid' : '')}`}
                  id="description"
                  name="description"
                />
                <ErrorMessage name="description" component="div" className="text-danger" />
              </Col>
            </Row>

            <Row className="mt-4 mb-2">
              <Label for="query" className="col-sm-2 col-form-label">
                <span className="d-inline-flex align-items-center gap-2">
                  <span>{t('SQL Query')}</span>
                  <button
                    type="button"
                    id="sql-query-help"
                    className="btn btn-sm btn-soft-secondary rounded-circle d-inline-flex align-items-center justify-content-center"
                    style={{
                      width: '1.75rem',
                      height: '1.75rem',
                      padding: 0,
                      lineHeight: 1,
                      fontWeight: 700,
                    }}
                    aria-label={t('SQL variable help')}
                  >
                    ?
                  </button>
                </span>
              </Label>
              <Col sm={10}>
                <div className="d-flex flex-wrap gap-2 mb-3">
                  {variableShortcuts.map((shortcut) => (
                    <button
                      key={shortcut.key}
                      type="button"
                      className="btn btn-sm btn-soft-secondary"
                      onClick={() => (
                        insertVariableTemplate(values.query, shortcut.template, setFieldValue)
                      )}
                    >
                      {shortcut.label}
                    </button>
                  ))}
                </div>
                <UncontrolledPopover
                  className="sql-query-help-popover"
                  trigger="legacy"
                  placement="right-start"
                  target="sql-query-help"
                >
                  <PopoverBody className="small" style={{ maxWidth: '28rem' }}>
                    <div className="mb-2"><strong>{t('Stored variables')}</strong></div>
                    <div className="mb-2">
                      {t('Define variables at the top with a comment line, then reference them in the query body with double curly braces.')}
                    </div>
                    <pre className="mb-2"><code>{variableExample}</code></pre>
                    <div className="mb-2"><strong>{t('Types')}</strong></div>
                    <div className="mb-2">
                      {t('Supported hints are date, datetime-local, number, text, tags_any, and tags_all. If omitted, the editor will still try to infer simple values.')}
                    </div>
                    <div className="mb-2"><strong>{t('Tag Variables')}</strong></div>
                    <div className="mb-2">
                      {t('Use tags_any inside IN (...). Use tags_all as a full WHERE fragment. Tag definitions should store tag labels, not ids.')}
                    </div>
                    <pre className="mb-2"><code>{tagVariableExample}</code></pre>
                    <div className="mb-2">
                      {t('The alias style is table_name(foreign_key=entity_alias.id,tag_id=tag_id). tags_any uses the selected tag ids in IN (...), while tags_all renders AND-joined EXISTS clauses against that bridge table.')}
                    </div>
                    <div className="mb-2"><strong>{t('Relative date defaults')}</strong></div>
                    <div className="mb-2">
                      {t("Examples: 'today', 'yesterday', 'tomorrow', '1 day ago', '2 weeks ago', '1 month from now'.")}
                    </div>
                    <div className="mb-0"><strong>{t('Quoting')}</strong></div>
                    <div>
                      {t('Do not add extra quotes around {{variables}} unless you really need them. The renderer already injects quoted literals for date, datetime, and text values.')}
                    </div>
                  </PopoverBody>
                </UncontrolledPopover>
                <SqlEditor
                  value={values.query || ''}
                  placeholder={t('Write or paste SQL here')}
                  invalid={Boolean(errors.query && touched.query)}
                  onChange={(nextValue) => setFieldValue('query', nextValue)}
                />
                <ErrorMessage name="query" component="div" className="text-danger mt-2" />
                <small className="text-muted d-block mt-2">
                  {t('Highlighting emphasizes keywords, constants, comments, strings, numbers, and backtick identifiers.')}
                </small>
              </Col>
            </Row>

            {loading && (
              <Alert color="info" className="mt-3 mb-0">
                {t('Saving...')}
              </Alert>
            )}
          </Form>
        )}
      </Formik>
    </>
  );
}

SQLForm.defaultProps = {
  t: (x) => (x),
  entity: {
    name: '',
    description: '',
    query: '',
  },
  onSuccess: () => {},
};

SQLForm.propTypes = {
  t: PropTypes.any,
  entity: PropTypes.object,
  onSuccess: PropTypes.func,
};

export default connect(() => ({

}), {})(withTranslation()(SQLForm));
